import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    User,
    MapPin,
    Briefcase,
    GraduationCap,
    Heart,
    Users,
    Building,
    Save,
    Camera
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EDUCATION_OPTIONS = [
    'High School',
    'Some College',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'Doctorate',
    'Other'
];

const MARITAL_STATUS_OPTIONS = [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' },
    { value: 'separated', label: 'Separated' }
];

const ENTREPRENEUR_STATUS_OPTIONS = [
    { value: 'yes', label: 'Yes, I am an entrepreneur' },
    { value: 'aspiring', label: 'Aspiring entrepreneur' },
    { value: 'no', label: 'No' }
];

const INCOME_RANGE_OPTIONS = [
    { value: 'under_25k', label: 'Under $25,000' },
    { value: '25k_50k', label: '$25,000 - $50,000' },
    { value: '50k_75k', label: '$50,000 - $75,000' },
    { value: '75k_100k', label: '$75,000 - $100,000' },
    { value: '100k_150k', label: '$100,000 - $150,000' },
    { value: '150k_200k', label: '$150,000 - $200,000' },
    { value: 'over_200k', label: 'Over $200,000' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

const EMPLOYMENT_STATUS_OPTIONS = [
    { value: 'employed', label: 'Employed' },
    { value: 'self-employed', label: 'Self-Employed' },
    { value: 'unemployed', label: 'Unemployed' },
    { value: 'retired', label: 'Retired' },
    { value: 'student', label: 'Student' }
];

const COMMITTEE_OPTIONS = [
    'Mentorship (Brother 2 Brother)',
    'Fundraising',
    'Community Engagement',
    'Professional Development',
    'Recruitment',
    'IT/Marketing'
];

const INTEREST_OPTIONS = [
    'Mental health & wellness',
    'Professional networking',
    'Mentorship',
    'Community service',
    'Personal growth',
    'Spiritual development',
    'Financial wellness',
    'Physical fitness',
    'Creative expression',
    'Leadership development'
];

const MemberProfilePage = () => {
    const { member, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        profession: '',
        location: '',
        birth_city: '',
        education: '',
        marital_status: '',
        has_children: false,
        num_children: 0,
        has_siblings: false,
        num_siblings: 0,
        hobbies: [],
        entrepreneur_status: '',
        employment_status: '',
        employer: '',
        income_range: '',
        interests: [],
        committee_interest: [],
        profile_image: '',
        bio: ''
    });
    const [hobbyInput, setHobbyInput] = useState('');

    useEffect(() => {
        if (member) {
            setFormData({
                name: member.name || '',
                phone: member.phone || '',
                profession: member.profession || '',
                location: member.location || '',
                birth_city: member.birth_city || '',
                education: member.education || '',
                marital_status: member.marital_status || '',
                has_children: member.has_children || false,
                num_children: member.num_children || 0,
                has_siblings: member.has_siblings || false,
                num_siblings: member.num_siblings || 0,
                hobbies: member.hobbies || [],
                entrepreneur_status: member.entrepreneur_status || '',
                employment_status: member.employment_status || '',
                employer: member.employer || '',
                income_range: member.income_range || '',
                interests: member.interests || [],
                committee_interest: member.committee_interest || [],
                profile_image: member.profile_image || '',
                bio: member.bio || ''
            });
        }
    }, [member]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleArrayItem = (field, item) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].includes(item)
                ? prev[field].filter(i => i !== item)
                : [...prev[field], item]
        }));
    };

    const addHobby = () => {
        if (hobbyInput.trim() && !formData.hobbies.includes(hobbyInput.trim())) {
            setFormData(prev => ({
                ...prev,
                hobbies: [...prev.hobbies, hobbyInput.trim()]
            }));
            setHobbyInput('');
        }
    };

    const removeHobby = (hobby) => {
        setFormData(prev => ({
            ...prev,
            hobbies: prev.hobbies.filter(h => h !== hobby)
        }));
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            toast.error('Please upload a JPEG, PNG, GIF, or WebP image');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
        }

        setUploadingPhoto(true);
        try {
            // Get Cloudinary signature
            const sigResponse = await axios.get(`${API}/cloudinary/signature?folder=profiles`);
            const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

            // Upload directly to Cloudinary
            const cloudinaryFormData = new FormData();
            cloudinaryFormData.append('file', file);
            cloudinaryFormData.append('api_key', api_key);
            cloudinaryFormData.append('timestamp', timestamp);
            cloudinaryFormData.append('signature', signature);
            cloudinaryFormData.append('folder', folder);

            const cloudinaryResponse = await fetch(
                `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
                { method: 'POST', body: cloudinaryFormData }
            );

            const cloudinaryData = await cloudinaryResponse.json();

            if (cloudinaryData.error) {
                throw new Error(cloudinaryData.error.message);
            }

            // Update profile with Cloudinary URL
            const photoUrl = cloudinaryData.secure_url;
            const response = await axios.put(`${API}/member/profile`, { profile_image: photoUrl });

            // Update local state and auth context
            setFormData(prev => ({ ...prev, profile_image: photoUrl }));
            setImageLoadError(false);
            updateUser(response.data);
            toast.success('Profile photo updated!');
        } catch (error) {
            console.error('Error uploading photo:', error);
            toast.error(error.message || 'Failed to upload photo');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const response = await axios.put(`${API}/member/profile`, formData);
            updateUser(response.data);
            toast.success('Profile updated successfully!');
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 max-w-4xl" data-testid="member-profile">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-white font-['Playfair_Display'] flex items-center gap-3">
                    <User className="w-8 h-8 text-[#C9A227]" />
                    My Profile
                </h1>
                <p className="text-[#A0A0A0] mt-1">
                    Complete your profile to connect better with the community
                </p>
            </div>

            {/* Profile Picture */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <Camera className="w-5 h-5 text-[#C9A227]" />
                        Profile Picture
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-6">
                        {formData.profile_image && !imageLoadError ? (
                            <img 
                                src={formData.profile_image.startsWith('/api/uploads') 
                                    ? `${process.env.REACT_APP_BACKEND_URL}${formData.profile_image}`
                                    : formData.profile_image.startsWith('/uploads')
                                        ? `${process.env.REACT_APP_BACKEND_URL}/api${formData.profile_image}` 
                                        : formData.profile_image
                                } 
                                alt="Profile"
                                className="w-24 h-24 rounded-xl object-cover bg-[#3D4435]"
                                onError={() => setImageLoadError(true)}
                                onLoad={() => setImageLoadError(false)}
                            />
                        ) : (
                            <div className="w-24 h-24 rounded-xl bg-[#C9A227] flex items-center justify-center text-[#3D4435] text-3xl font-bold">
                                {formData.name?.charAt(0).toUpperCase() || 'M'}
                            </div>
                        )}
                        <div className="flex-1">
                            <Label className="text-white mb-2 block">Upload Photo</Label>
                            <div className="flex items-center gap-3">
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif,image/webp"
                                        onChange={handlePhotoUpload}
                                        className="hidden"
                                        data-testid="profile-photo-upload"
                                    />
                                    <div className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        uploadingPhoto 
                                            ? 'bg-[#5A6350] text-[#A0A0A0] cursor-not-allowed' 
                                            : 'bg-[#C9A227] text-[#3D4435] hover:bg-[#B8922A]'
                                    }`}>
                                        {uploadingPhoto ? 'Uploading...' : 'Choose Photo'}
                                    </div>
                                </label>
                                {formData.profile_image && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleInputChange('profile_image', '')}
                                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                    >
                                        Remove
                                    </Button>
                                )}
                            </div>
                            <p className="text-[#666] text-xs mt-2">JPEG, PNG, GIF, or WebP (max 5MB)</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Bio Section */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <Heart className="w-5 h-5 text-[#C9A227]" />
                        About Me
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        <Label className="text-white">Bio</Label>
                        <Textarea
                            value={formData.bio}
                            onChange={(e) => handleInputChange('bio', e.target.value)}
                            placeholder="Tell the community a bit about yourself, your journey, and what brings you to the Recovery Room..."
                            className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] min-h-[120px]"
                            data-testid="profile-bio-input"
                        />
                        <p className="text-[#666] text-xs">Share your story with fellow brothers in the community</p>
                    </div>
                </CardContent>
            </Card>

            {/* Basic Info */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <User className="w-5 h-5 text-[#C9A227]" />
                        Basic Information
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-white">Full Name</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className="bg-[#3D4435] border-[#5A6350] text-white"
                                data-testid="profile-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Phone</Label>
                            <Input
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                placeholder="(555) 123-4567"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="profile-phone-input"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-white flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#C9A227]" />
                                Current Location
                            </Label>
                            <Input
                                value={formData.location}
                                onChange={(e) => handleInputChange('location', e.target.value)}
                                placeholder="Washington, DC"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="profile-location-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Birth City</Label>
                            <Input
                                value={formData.birth_city}
                                onChange={(e) => handleInputChange('birth_city', e.target.value)}
                                placeholder="Where were you born?"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="profile-birth-city-input"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Professional Info */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-[#C9A227]" />
                        Professional Background
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-white">Profession</Label>
                            <Input
                                value={formData.profession}
                                onChange={(e) => handleInputChange('profession', e.target.value)}
                                placeholder="Your current profession"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="profile-profession-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white flex items-center gap-2">
                                <Building className="w-4 h-4 text-[#C9A227]" />
                                Employer
                            </Label>
                            <Input
                                value={formData.employer}
                                onChange={(e) => handleInputChange('employer', e.target.value)}
                                placeholder="Current employer"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                data-testid="profile-employer-input"
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white">Employment Status</Label>
                        <div className="flex flex-wrap gap-2">
                            {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleInputChange('employment_status', option.value)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.employment_status === option.value
                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white">Entrepreneur Status</Label>
                        <div className="flex flex-wrap gap-2">
                            {ENTREPRENEUR_STATUS_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleInputChange('entrepreneur_status', option.value)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.entrepreneur_status === option.value
                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white">Annual Income Range</Label>
                        <select
                            value={formData.income_range}
                            onChange={(e) => handleInputChange('income_range', e.target.value)}
                            className="w-full md:w-1/2 h-10 px-3 rounded-md bg-[#3D4435] border border-[#5A6350] text-white focus:ring-[#C9A227]/30 focus:border-[#C9A227] outline-none"
                            data-testid="profile-income-range-select"
                        >
                            <option value="" className="bg-[#3D4435]">Select income range</option>
                            {INCOME_RANGE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value} className="bg-[#3D4435]">
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </CardContent>
            </Card>

            {/* Education & Personal */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-[#C9A227]" />
                        Education & Personal
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-white">Education Level</Label>
                        <div className="flex flex-wrap gap-2">
                            {EDUCATION_OPTIONS.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => handleInputChange('education', option)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.education === option
                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                    }`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white">Marital Status</Label>
                        <div className="flex flex-wrap gap-2">
                            {MARITAL_STATUS_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => handleInputChange('marital_status', option.value)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.marital_status === option.value
                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                    }`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="has_children"
                                    checked={formData.has_children}
                                    onChange={(e) => handleInputChange('has_children', e.target.checked)}
                                    className="w-4 h-4 rounded border-[#5A6350] bg-[#3D4435] text-[#C9A227] focus:ring-[#C9A227]"
                                />
                                <Label htmlFor="has_children" className="text-white cursor-pointer">I have children</Label>
                            </div>
                            {formData.has_children && (
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.num_children}
                                    onChange={(e) => handleInputChange('num_children', parseInt(e.target.value) || 0)}
                                    placeholder="Number of children"
                                    className="bg-[#3D4435] border-[#5A6350] text-white w-full"
                                />
                            )}
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="has_siblings"
                                    checked={formData.has_siblings}
                                    onChange={(e) => handleInputChange('has_siblings', e.target.checked)}
                                    className="w-4 h-4 rounded border-[#5A6350] bg-[#3D4435] text-[#C9A227] focus:ring-[#C9A227]"
                                />
                                <Label htmlFor="has_siblings" className="text-white cursor-pointer">I have siblings</Label>
                            </div>
                            {formData.has_siblings && (
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.num_siblings}
                                    onChange={(e) => handleInputChange('num_siblings', parseInt(e.target.value) || 0)}
                                    placeholder="Number of siblings"
                                    className="bg-[#3D4435] border-[#5A6350] text-white w-full"
                                />
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Hobbies */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <Heart className="w-5 h-5 text-[#C9A227]" />
                        Hobbies & Interests
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label className="text-white">Add Hobbies</Label>
                        <div className="flex gap-2">
                            <Input
                                value={hobbyInput}
                                onChange={(e) => setHobbyInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHobby())}
                                placeholder="Enter a hobby"
                                className="bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                            />
                            <Button
                                type="button"
                                onClick={addHobby}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                            >
                                Add
                            </Button>
                        </div>
                        {formData.hobbies.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                {formData.hobbies.map((hobby, idx) => (
                                    <span 
                                        key={idx}
                                        className="px-3 py-1 bg-[#C9A227]/20 text-[#C9A227] rounded-full text-sm flex items-center gap-2"
                                    >
                                        {hobby}
                                        <button
                                            type="button"
                                            onClick={() => removeHobby(hobby)}
                                            className="hover:text-red-400"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="space-y-2">
                        <Label className="text-white">Areas of Interest</Label>
                        <div className="flex flex-wrap gap-2">
                            {INTEREST_OPTIONS.map((interest) => (
                                <button
                                    key={interest}
                                    type="button"
                                    onClick={() => toggleArrayItem('interests', interest)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        formData.interests.includes(interest)
                                            ? 'bg-[#C9A227] text-[#3D4435]'
                                            : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                    }`}
                                >
                                    {interest}
                                </button>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Committee Interest */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#C9A227]" />
                        Committee Interest
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-[#A0A0A0] text-sm mb-4">
                        Select any committees you'd be interested in joining
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {COMMITTEE_OPTIONS.map((committee) => (
                            <button
                                key={committee}
                                type="button"
                                onClick={() => toggleArrayItem('committee_interest', committee)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                                    formData.committee_interest.includes(committee)
                                        ? 'bg-[#C9A227] text-[#3D4435]'
                                        : 'bg-[#3D4435] text-[#A0A0A0] hover:bg-[#5A6350]'
                                }`}
                            >
                                {committee}
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
                <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold px-8"
                    data-testid="save-profile-btn"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Profile'}
                </Button>
            </div>
        </div>
    );
};

export default MemberProfilePage;
